import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { PaymentsConfigStatus, PlatformSettings } from '../types/admin';
import {
  FEE_MOMENTS,
  environmentSummary,
  formatAcquiringRates,
  overridesFromSettings,
  validateOverrides,
  applyOverridesToSettings,
  type SafeDealOverrideState,
} from './adminSettingsView';

/* ============================================================
   AS-1 (TASK-6e4bd7b5): payments & notifications section of the
   admin settings hub. Read-only environment status (effective
   Safe Deal values, masked YooKassa/email presence — secret
   VALUES never arrive to the client) + Safe Deal overrides with
   «Конфиг | Вкл | Выкл» tri-state semantics: null follows the
   appsettings deployment default. DESIGN-SYSTEM: light tokens.
   ============================================================ */

const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-separator)',
  borderRadius: 'var(--radius-lg)',
  padding: '24px',
  maxWidth: '640px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  fontSize: 'var(--fs-caption)',
  fontWeight: 'var(--fw-semibold)',
  color: 'var(--color-text-secondary)',
};

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--color-separator)',
  borderRadius: '12px',
  padding: '10px 12px',
  fontSize: 'var(--fs-body)',
  fontWeight: 'var(--fw-regular)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
};

/** Tri-state control for a nullable boolean override. */
function TriStateSwitch({
  label, value, configValue, onChange, labels,
}: {
  label: string;
  value: boolean | null;
  configValue: boolean | undefined;
  onChange: (next: boolean | null) => void;
  labels: [string, string];
}) {
  return (
    <div style={{ ...fieldStyle, gap: '8px' }}>
      <span>{label}</span>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {([
          { key: 'config', active: value === null, text: `Конфиг (${configValue ? labels[0] : labels[1]})`, next: null as boolean | null },
          { key: 'on', active: value === true, text: labels[0], next: true },
          { key: 'off', active: value === false, text: labels[1], next: false },
        ]).map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.next)}
            style={{
              border: '1px solid var(--color-separator)',
              borderRadius: '999px',
              padding: '8px 16px',
              fontSize: 'var(--fs-caption)',
              fontWeight: 'var(--fw-semibold)',
              cursor: 'pointer',
              background: opt.active ? 'var(--color-primary)' : 'var(--color-surface)',
              color: opt.active ? '#fff' : 'var(--color-text-primary)',
            }}
          >
            {opt.text}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Override wrapper: checkbox «Переопределить» + children inputs; off = null (follow config). */
function OverrideField({
  label, hint, overridden, onToggle, children,
}: {
  label: string;
  hint?: string;
  overridden: boolean;
  onToggle: (on: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ ...fieldStyle, gap: '8px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <input type="checkbox" checked={overridden} onChange={(e) => onToggle(e.target.checked)} />
        {label}
        <span
          style={{
            fontSize: 'var(--fs-caption)',
            fontWeight: 'var(--fw-regular)',
            color: overridden ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            border: `1px solid ${overridden ? 'var(--color-primary)' : 'var(--color-separator)'}`,
            borderRadius: '999px',
            padding: '2px 10px',
          }}
        >
          {overridden ? 'переопределено' : 'из конфигурации'}
        </span>
      </label>
      {hint && !overridden && <span style={{ fontWeight: 'var(--fw-regular)' }}>{hint}</span>}
      {overridden && children}
    </div>
  );
}

export function AdminPaymentsPanel({
  settings, onSave,
}: {
  settings: PlatformSettings;
  onSave: (next: PlatformSettings) => Promise<void>;
}) {
  const [status, setStatus] = useState<PaymentsConfigStatus | null>(null);
  const [state, setState] = useState<SafeDealOverrideState>(() => overridesFromSettings(settings));
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.getAdminPaymentsConfig().then(setStatus).catch(() => setStatus(null));
  }, []);

  const patch = useCallback((next: Partial<SafeDealOverrideState>) => {
    setState((prev) => ({ ...prev, ...next }));
    setErrors([]);
  }, []);

  const save = async () => {
    const validation = validateOverrides(state);
    setErrors(validation);
    if (validation.length > 0) return;
    setSaving(true);
    try {
      await onSave(applyOverridesToSettings(settings, state));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ fontSize: 'var(--fs-title)', fontWeight: 'var(--fw-bold)', color: 'var(--color-text-primary)' }}>
        Платежи и уведомления
      </h3>

      {status && (
        <div style={{ ...fieldStyle, background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', padding: '12px 16px' }}>
          <span>Текущее окружение</span>
          {environmentSummary(status).map((line) => (
            <span key={line} style={{ fontWeight: 'var(--fw-regular)', color: 'var(--color-text-primary)' }}>
              {line}
            </span>
          ))}
          <span style={{ fontWeight: 'var(--fw-regular)', color: 'var(--color-text-secondary)' }}>
            Секретные ключи хранятся только в настройках окружения и не отображаются.
          </span>
        </div>
      )}

      <TriStateSwitch
        label="Safe Deal (безопасная сделка)"
        value={state.enabled}
        configValue={status?.safeDealConfigEnabled}
        onChange={(next) => patch({ enabled: next })}
        labels={['включён', 'выключен']}
      />

      <OverrideField
        label="fee_moment — момент комиссии"
        hint={status ? `Сейчас: ${status.safeDealEffectiveFeeMoment}` : undefined}
        overridden={state.feeMoment !== null}
        onToggle={(on) => patch({ feeMoment: on ? status?.safeDealEffectiveFeeMoment ?? 'deal_closed' : null })}
      >
        <select
          value={state.feeMoment ?? 'deal_closed'}
          onChange={(e) => patch({ feeMoment: e.target.value })}
          style={inputStyle}
        >
          {FEE_MOMENTS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </OverrideField>

      <OverrideField
        label="Комиссия платформы (доля от заказа)"
        hint={status ? `Сейчас: ${(status.safeDealEffectiveCommissionRate * 100).toFixed(1).replace(/\.0$/, '')}% (0.10 = 10%)` : undefined}
        overridden={state.commissionRate !== null}
        onToggle={(on) => patch({ commissionRate: on ? status?.safeDealEffectiveCommissionRate ?? 0.1 : null })}
      >
        <input
          type="number"
          min="0.001"
          max="0.5"
          step="0.001"
          value={state.commissionRate ?? 0.1}
          onChange={(e) => patch({ commissionRate: Number(e.target.value) })}
          style={inputStyle}
        />
      </OverrideField>

      <OverrideField
        label="Тарифы эквайринга (0.4%–4%, ключ default обязателен)"
        hint={status ? 'Например: default = 4%, bank_card = 2.8%' : undefined}
        overridden={state.acquiringRatesText !== null}
        onToggle={(on) => patch({ acquiringRatesText: on ? formatAcquiringRates(settings.safeDealAcquiringRatesJson ?? null) || 'default = 4%\nbank_card = 2.8%' : null })}
      >
        <textarea
          rows={3}
          value={state.acquiringRatesText ?? ''}
          onChange={(e) => patch({ acquiringRatesText: e.target.value })}
          style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
        />
      </OverrideField>

      <OverrideField
        label="Срок сделки (дней, 1–180; информационно)"
        hint={status ? `Сейчас: ${status.safeDealEffectiveDealTtlDays} дн.` : undefined}
        overridden={state.dealTtlDays !== null}
        onToggle={(on) => patch({ dealTtlDays: on ? status?.safeDealEffectiveDealTtlDays ?? 90 : null })}
      >
        <input
          type="number"
          min="1"
          max="180"
          value={state.dealTtlDays ?? 90}
          onChange={(e) => patch({ dealTtlDays: Number(e.target.value) })}
          style={inputStyle}
        />
      </OverrideField>

      <TriStateSwitch
        label="Ежедневные автовыплаты исполнителям"
        value={state.autoPayoutEnabled}
        configValue={status?.safeDealEffectiveAutoPayoutEnabled}
        onChange={(next) => patch({ autoPayoutEnabled: next })}
        labels={['включены', 'выключены']}
      />

      <OverrideField
        label="Холд перед выплатой (часы после Completed)"
        hint={status ? `Сейчас: ${status.safeDealEffectiveAutoPayoutDelayHours} ч` : undefined}
        overridden={state.autoPayoutDelayHours !== null}
        onToggle={(on) => patch({ autoPayoutDelayHours: on ? status?.safeDealEffectiveAutoPayoutDelayHours ?? 24 : null })}
      >
        <input
          type="number"
          min="0"
          max="720"
          value={state.autoPayoutDelayHours ?? 24}
          onChange={(e) => patch({ autoPayoutDelayHours: Number(e.target.value) })}
          style={inputStyle}
        />
      </OverrideField>

      {errors.length > 0 && (
        <ul style={{ ...fieldStyle, color: '#c0392b', paddingLeft: '20px', fontWeight: 'var(--fw-regular)' }}>
          {errors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}

      <button
        type="button"
        disabled={saving}
        onClick={() => void save()}
        style={{
          alignSelf: 'flex-start',
          border: 'none',
          borderRadius: '999px',
          background: 'var(--color-primary)',
          color: '#fff',
          padding: '12px 24px',
          fontSize: 'var(--fs-body)',
          fontWeight: 'var(--fw-semibold)',
          cursor: saving ? 'default' : 'pointer',
        }}
      >
        {saving ? 'Сохранение…' : 'Сохранить платежные настройки'}
      </button>
    </div>
  );
}
